import { useMemo, useState, useEffect } from 'react';
import { ResultsTab } from '../types';
import { useLeaderboardParticipants } from '../../../hooks/useLeaderboardParticipants';
import { useLeaderboardDistricts } from '../../../hooks/useLeaderboardDistricts';
import { useLeaderboardOffices } from '../../../hooks/useLeaderboardOffices';
import { useLeaderboardEventsList } from '../../../hooks/useLeaderboardEventsList';
import { useLeaderboardSummary } from '../../../hooks/useLeaderboardSummary';
import { useActiveLeaderboardEvent } from '../../../hooks/useActiveLeaderboardEvent';

export const useAdminResultsContent = () => {
  const { events } = useLeaderboardEventsList();
  const { activeEvent } = useActiveLeaderboardEvent();
  const [activeTab, setActiveTab] = useState<ResultsTab>('participants');
  const [selectedChallenge, setSelectedChallenge] = useState('');

  const [isCasesModalOpen, setIsCasesModalOpen] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);

  useEffect(() => {
    if (events.length > 0 && !selectedChallenge) {
      setSelectedChallenge(events[0].id.toString());
    }
  }, [events, selectedChallenge]);

  const challengeOptions = useMemo(() => {
    return events.map((event) => ({
      value: event.id.toString(),
      label: event.name,
    }));
  }, [events]);

  const selectedEventId = selectedChallenge ? parseInt(selectedChallenge, 10) : undefined;
  const { summary } = useLeaderboardSummary(selectedEventId);

  const {
    participants,
    totalPages: participantsTotalPages,
    pageNumber: participantsPage,
    setPageNumber: setParticipantsPage,
    isLoading: participantsLoading,
    refetch: refetchParticipants,
  } = useLeaderboardParticipants(selectedEventId);
  const {
    districts,
    totalPages: districtsTotalPages,
    pageNumber: districtsPage,
    setPageNumber: setDistrictsPage,
    isLoading: districtsLoading,
    refetch: refetchDistricts,
  } = useLeaderboardDistricts(selectedEventId);
  const {
    offices,
    totalPages: officesTotalPages,
    pageNumber: officesPage,
    setPageNumber: setOfficesPage,
    isLoading: officesLoading,
    refetch: refetchOffices,
  } = useLeaderboardOffices(selectedEventId);

  const currentData = useMemo(() => {
    if (activeTab === 'participants') {
      return (participants || []).map((p) => ({
        id: p.id,
        rank: p.rank,
        participant: `${p.firstName} ${p.lastName}`,
        hours: String(p.totalHours ?? 0),
        clients: String(p.totalClients ?? 0),
        totalReviews: String(p.reviewsCount ?? 0),
        score: `${p.totalScore ?? 0} points`,
        area: p.districtNames || '',
        office: p.officeNames || '',
        agency: p.agencyNames || '',
        lastUpdate: new Date(p.lastUpdated).toLocaleDateString(),
      }));
    }
    if (activeTab === 'areas') {
      return (districts || []).map((d) => ({
        id: d.id,
        rank: d.rank,
        area: d.districtName,
        hours: String(d.totalHours ?? 0),
        clients: String(d.totalClients ?? 0),
        totalReviews: String(d.totalReviews ?? 0),
        score: `${d.totalScore ?? 0} points`,
        lastUpdate: new Date(d.lastUpdated).toLocaleDateString(),
      }));
    }
    return (offices || []).map((o) => ({
      id: o.id,
      rank: o.rank,
      office: o.name,
      hours: String(o.totalHours ?? 0),
      clients: String(o.totalClients ?? 0),
      totalReviews: String(o.totalReviews ?? 0),
      score: `${o.totalScore ?? 0} points`,
      area: o.districtName || '',
      agency: o.agencyName || '',
      lastUpdate: new Date(o.lastUpdated).toLocaleDateString(),
    }));
  }, [activeTab, participants, districts, offices]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as ResultsTab);
    setParticipantsPage(1);
    setDistrictsPage(1);
    setOfficesPage(1);
  };
  useEffect(() => {
    if (!selectedEventId) return;
    if (activeTab === 'participants') refetchParticipants();
    else if (activeTab === 'areas') refetchDistricts();
    else refetchOffices();
  }, [activeTab, selectedEventId]);

  const handleChallengeChange = (value: string) => {
    setSelectedChallenge(value);
    setParticipantsPage(1);
    setDistrictsPage(1);
    setOfficesPage(1);
  };

  const handlePageChange = (page: number) => {
    if (activeTab === 'participants') setParticipantsPage(page);
    else if (activeTab === 'areas') setDistrictsPage(page);
    else setOfficesPage(page);
  };

  const closeCasesModal = () => {
    setIsCasesModalOpen(false);
    setSelectedUserName(null);
  };

  const totalPages =
    activeTab === 'participants'
      ? participantsTotalPages
      : activeTab === 'areas'
        ? districtsTotalPages
        : officesTotalPages;
  const currentPageNumber =
    activeTab === 'participants'
      ? participantsPage
      : activeTab === 'areas'
        ? districtsPage
        : officesPage;

  const isLoading = useMemo(() => {
    if (activeTab === 'participants') return participantsLoading;
    if (activeTab === 'areas') return districtsLoading;
    if (activeTab === 'offices') return officesLoading;
    return false;
  }, [activeTab, participantsLoading, districtsLoading, officesLoading]);

  return {
    activeTab,
    selectedChallenge,
    challengeOptions,
    summary,
    currentData,
    totalPages,
    currentPageNumber,
    activeEvent,
    isLoading,
    isCasesModalOpen,
    selectedUserName,
    handleTabChange,
    handleChallengeChange,
    handlePageChange,
    closeCasesModal,
    setIsCasesModalOpen,
    setSelectedUserName,
  };
};
